<!-- Pager — the home screen IS the pager (IDEATION → Key screens 1).

     Root tier, but WITHOUT <AppLargeTitle>: on iOS the large title is a 52pt
     block above the device that says "Page Me" a second time and pushes the
     one thing this screen is for below the fold on the tall two-way devices.
     The bar carries the name; the device is the first useful control. (The
     recipe's anti-pattern list names exactly this: a decorative header above
     the first useful control.)

     ONE trailing header action, like every other screen: Friends (badge =
     pending requests). Devices and Settings are on the pager itself, as the
     MODE and SETUP hardware keys (PagerDevice), so the bar keeps the
     two-tier contract and the device stays the thing you operate.

     Below the device: at most one app-voice line and at most one primary
     button — the activation / empty-state action. Nothing else. -->
<template>
  <IonPage>
    <AppHeader title="Page Me">
      <IonButtons slot="end" class="pm-pager-actions">
        <IonButton class="pm-pager-act" @click="go('/tabs/friends')">
          <span class="pm-pager-act__in">
            <span class="pm-pager-act__icon">
              <IonIcon :icon="peopleOutline" aria-hidden="true" />
              <span v-if="pendingCount" class="pm-pager-act__badge" aria-hidden="true">{{ pendingCount }}</span>
            </span>
            <span class="pm-pager-act__label">Friends</span>
            <span v-if="pendingCount" class="sr-only">, {{ pendingCount }} {{ pendingCount === 1 ? 'request' : 'requests' }}</span>
          </span>
        </IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true" class="pm-pager-content">
      <div class="pm-pager" :class="{ 'pm-pager--large': settings.largeControls, 'pm-pager--cta': !!cta || askName }">
        <section class="pm-pager__stage" :aria-label="`${shownDevice.name} pager`">
          <Transition name="pm-dev-swap" mode="out-in">
            <PagerDevice
              :key="shownDevice.id"
              :device="shownDevice"
              :large-controls="settings.largeControls"
              :active="active"
              :first-run="firstRun"
              :hold-number="holdNumber"
            />
          </Transition>
        </section>

        <p v-if="line" class="pm-pager__line">{{ line }}</p>

        <!-- One step, skippable: the name friends see instead of the number.
             It takes the primary slot until it is answered. -->
        <form v-if="askName" class="pm-scr-namestep pm-pager__cta" novalidate @submit.prevent="saveName.run()">
          <label class="pm-scr-label" for="pm-pager-name">What should friends see?</label>
          <input
            id="pm-pager-name"
            v-model="nameText"
            class="pm-scr-input pm-scr-input--lcd pm-scr-upper"
            type="text"
            maxlength="12"
            autocomplete="nickname"
            autocapitalize="characters"
            enterkeyhint="done"
            placeholder="YOUR NAME"
            aria-describedby="pm-pager-name-help"
            @input="nameText = nameText.toUpperCase().replace(/[^A-Z0-9 .'_-]/g, '')"
          >
          <p id="pm-pager-name-help" class="pm-scr-muted pm-scr-small">Your name goes with your number on every page. Up to 12 letters — you can change it in Settings.</p>
          <p v-if="saveName.error.value" class="field-error" role="alert">{{ saveName.error.value }}</p>
          <div class="pm-scr-button-row">
            <IonButton type="submit" :disabled="saveName.busy.value || !nameText.trim()">Save name</IonButton>
            <IonButton type="button" fill="clear" @click="dismissNameOffer()">Skip</IonButton>
          </div>
        </form>

        <div v-else-if="cta" class="pm-pager__cta">
          <IonButton expand="block" :disabled="retry.busy.value" @click="cta.run()">
            {{ cta.label }}
          </IonButton>
          <p v-if="retry.error.value" class="pm-pager__error">{{ retry.error.value }}</p>
        </div>
      </div>

      <ActivationCard :is-open="cardOpen" @close="cardOpen = false" />
      <BeepsPrompt :is-open="beepsOpen" @close="beepsOpen = false" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon,
  onIonViewDidEnter, onIonViewWillLeave,
} from '@ionic/vue';
import { useRouter } from 'vue-router';
import { peopleOutline } from 'ionicons/icons';
import { AppHeader, useAction, showUntilLearned } from '../core/ui';
import { isNative } from '../core/native.js';
import PagerDevice from '../components/PagerDevice.vue';
import ActivationCard from '../components/ActivationCard.vue';
import BeepsPrompt from '../components/BeepsPrompt.vue';
import {
  me, activation, pages, friends, settings, pendingIn, currentDevice,
  activationCardDue, beepsOffer, activate, nameOffer, setHandle, dismissNameOffer,
} from '../store.js';
import { userMessage } from '../components/user-message.js';
import { deviceById } from '../pm/devices.js';
import { audioUnlocked } from '../pm/beep.js';
import { OPERATOR_NUMBER } from '../pm/format.js';

const router = useRouter();
const go = (path) => router.push(path);

const pendingCount = computed(() => pendingIn.value?.length || 0);

// ── visibility: is this screen the one on top? ───────────────────────────
const active = ref(true);
onIonViewWillLeave(() => { active.value = false; });

// ── device swap: the old one slides out, the new one up — on return ──────
// Devices is pushed on top of this screen, so a change made there must not
// animate unseen underneath it. The swap waits until this screen is back.
const shownId = ref(settings.device);
const shownDevice = computed(() => deviceById(shownId.value) || currentDevice.value);
watch(() => settings.device, (id) => { if (active.value) shownId.value = id; });
onIonViewDidEnter(() => {
  active.value = true;
  if (shownId.value !== settings.device) shownId.value = settings.device;
});

// ── first run & empty state ──────────────────────────────────────────────
const hasNumber = computed(() => !!me.value?.number);
const everSent = computed(() => pages.value.some((p) => p.dir === 'out'));
const hasRealFriend = computed(() => friends.value.some((f) => f.status === 'accepted' && !f.system));
const firstRun = computed(() => hasNumber.value && !everSent.value && !pages.value.length);
const noService = computed(() => !hasNumber.value && ['offline', 'error'].includes(activation.value));

const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
function onOnline() {
  online.value = true;
  // "It'll try again when you're back online."
  if (!hasNumber.value) activate();
}
function onOffline() { online.value = false; }

// The one-time web line: browsers keep audio locked until a tap.
const audioHint = !isNative && showUntilLearned('pm-audio-tap', 1);

const line = computed(() => {
  if (noService.value) {
    return "Page Me needs a connection to get your number. It'll try again when you're back online.";
  }
  if (!hasNumber.value) return '';
  if (firstRun.value) return 'This is your pager number. Give it to friends so they can page you.';
  if (!online.value) return "No signal. Pages you send will go when you're back online.";
  if (settings.alertMode === 'silent') return 'Your pager is on silent. Press MODE to change it.';
  if (audioHint && !audioUnlocked.value && settings.alertMode === 'beep') return 'Tap anywhere to let your pager beep.';
  return '';
});

const retry = useAction(async () => {
  const r = await activate();
  if (r && r.ok === false && r.message && !r.network) throw new Error(r.message);
}, { weight: 'tick' });

const cta = computed(() => {
  if (noService.value) return { label: 'Try again', run: () => retry.run() };
  if (!hasNumber.value) return null;
  if (!everSent.value) return { label: 'Page the operator', run: () => go(`/tabs/compose/${OPERATOR_NUMBER}`) };
  if (!hasRealFriend.value) return { label: 'Add a friend', run: () => go('/tabs/friends') };
  return null;
});

// ── "What should friends see?" ───────────────────────────────────────────
const askName = computed(() => nameOffer.value && hasNumber.value && !noService.value);
const nameText = ref('');
// The LCD keeps YOUR NUMBER up while you name it, then shows NAME: SAM for a
// moment before it moves on to whatever has arrived.
const justNamed = ref(false);
const holdNumber = computed(() => askName.value || justNamed.value);
const saveName = useAction(async () => {
  const r = await setHandle(nameText.value);
  if (r && r.ok === false) throw new Error(userMessage(r, "Couldn't save your name. Try again."));
  justNamed.value = true;
  setTimeout(() => { justNamed.value = false; }, 3000);
}, { weight: 'pop' });

// ── sheets: the activation card, then the beeps pre-prompt ──────────────
// Latched locally so a sheet stays up to show its own outcome after the
// store's flag flips, and only presented while this screen is on top.
const cardOpen = ref(false);
const beepsOpen = ref(false);
watch([activationCardDue, active], ([due, on]) => {
  if (due && on && !beepsOpen.value) cardOpen.value = true;
}, { immediate: true });
watch([beepsOffer, active, cardOpen], ([due, on, card]) => {
  if (due && on && !card) beepsOpen.value = true;
}, { immediate: true });

onMounted(() => {
  activate();
  addEventListener('online', onOnline);
  addEventListener('offline', onOffline);
});
onBeforeUnmount(() => {
  removeEventListener('online', onOnline);
  removeEventListener('offline', onOffline);
});
</script>
